import { useTranslate } from 'src/localization/Trns'

import type { CardDetailHeroGroupState } from './useCardDetailHeroData'

/**
 * Group state → user-visible text, shared by the full hero and the pinned
 * compact bar so the two variants never word the same state differently.
 *
 * Every non-ready state is announced as *text*, never as a colour, an empty
 * space, or a dash — a placeholder the user cannot read is indistinguishable
 * from a zero balance.
 *
 * The literals stay inside `translate()` so the localization extractor can see
 * them; a lookup table keyed by state would defeat it, which is why these are
 * hooks returning mappers rather than plain objects.
 */

export const useLimitsStateText = (): ((
  state: CardDetailHeroGroupState,
) => string | undefined) => {
  const translate = useTranslate()

  return (state) => {
    switch (state) {
      case 'ready':
        return undefined
      case 'loading':
        return translate('Loading available credit')
      case 'unavailable':
        return translate('Available credit is unavailable for this card')
      case 'error':
        return translate('Available credit could not be loaded')
    }
  }
}

export const useIdentityStateText = (): ((
  state: CardDetailHeroGroupState,
) => string | undefined) => {
  const translate = useTranslate()

  return (state) => {
    switch (state) {
      case 'ready':
        return undefined
      case 'loading':
        return translate('Loading card details')
      // Identity has no capability gate, so `unavailable` is not reachable for
      // this group; it shares the error wording rather than going silent.
      case 'unavailable':
      case 'error':
        return translate('Card details could not be loaded')
    }
  }
}
